import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import {
  Box,
  StackLayout,
  Tabs,
  ParagraphText,
  styled,
} from '@leagueplatform/genesis-core';
import { SegmentedControl } from '@leagueplatform/genesis-core-alpha';
import { useUniqueId } from '@leagueplatform/web-common';
import { addToActionAnalytics } from '@leagueplatform/masonry-renderers';
import { ACCORDION_ANALYTICS_DETAIL } from '#constants/masonry.constants.ts';
import { SectionHeaderWithIcon } from '#components/section-header-with-icon/section-header-with-icon.component.tsx';
import { AccordionPlanUsage } from './components/accordion-plan-usage.component';
import { StaticPlanUsage } from './components/static-plan-usage.component';
import type {
  Heading,
  Icon as HeadingIcon,
  Network,
} from './plan-usage-widget.types';

export const NODE_TYPE_PLAN_USAGE_WIDGET = 'planUsageWidget';

type PlanUsageWidgetProps = {
  heading?: Heading;
  headingLeadingIcon?: HeadingIcon;
  defaultNetwork?: string;
  networks?: Network[];
};

export type PlanUsageWidgetNodeActions = {
  onNetworkClicked?: MasonryEngineNodeAction;
  onAccordionClicked?: MasonryEngineNodeAction;
  onTooltipClicked?: MasonryEngineNodeAction;
};

type PlanUsageWidgetNode = MasonryEngineNode<
  typeof NODE_TYPE_PLAN_USAGE_WIDGET,
  PlanUsageWidgetProps,
  PlanUsageWidgetNodeActions,
  {}
>;

export type PlanUsageWidgetRendererProps =
  MasonryEngineNodeRendererProps<PlanUsageWidgetNode>;

const PlanUsageContainer = styled(Box, {
  width: '100%',
  backgroundColor: '$surfaceBackgroundSecondary',
  borderRadius: '$card',
  borderWidth: '$card',
  borderStyle: 'solid',
  borderColor: '$onSurfaceBorderSubdued',
  padding: '$two',
});

export function PlanUsageWidgetRenderer({
  heading,
  headingLeadingIcon,
  defaultNetwork,
  networks: initialNetworks = [],
  onNetworkClicked,
  onAccordionClicked,
  onTooltipClicked,
  nodeId,
}: PlanUsageWidgetRendererProps) {
  const invokeAction = useMasonryEngineActionEmitter();
  const { heading: headingText, headingLevel = '2' } = heading || {};
  const uniqueId = useUniqueId({
    prefix: 'plan-usage-widget',
  });
  // Confirm defaultNetwork is in initialNetworks
  const defaultSelectedNetwork = React.useMemo(() => {
    const defaultNetworkType = initialNetworks.find(
      (network) => network.networkType === defaultNetwork,
    );

    if (!defaultNetworkType || !defaultNetwork) {
      return initialNetworks[0]?.networkType || '';
    }

    return defaultNetwork;
  }, [defaultNetwork, initialNetworks]);
  const networks = React.useMemo(() => initialNetworks, [initialNetworks]);

  const [selectedNetwork, setSelectedNetwork] = React.useState<string>(
    defaultSelectedNetwork,
  );

  // IDs for accessibility
  const headingId = `${uniqueId}-heading`;

  // Handle network selection
  const handleNetworkClick = React.useCallback(
    (data: { networkType: string; networkLabel: string }) =>
      (event: React.SyntheticEvent) => {
        setSelectedNetwork(data.networkType);
        if (onNetworkClicked) {
          invokeAction({
            event,
            ...addToActionAnalytics(onNetworkClicked, {
              detail: data.networkLabel,
              network_type: data.networkType,
            }),
          });
        }
      },
    [invokeAction, onNetworkClicked],
  );

  // Handle accordion click
  const handleAccordionClick = React.useCallback(
    (accordionId: string, newExpandedState: boolean) => {
      // Update the accordion's isExpanded state in the node state
      invokeAction({
        type: 'setNodeState',
        namespace: 'default',
        payload: {
          nodeId,
          state: {
            networks: networks.map((network) => ({
              ...network,
              planUsage: network.planUsage?.map((planUsage) => ({
                ...planUsage,
                accordion:
                  planUsage.accordion?.analyticsId === accordionId
                    ? { ...planUsage.accordion, isExpanded: newExpandedState }
                    : planUsage.accordion,
              })),
            })),
          },
        },
      });

      // Invoke the custom accordion clicked action if provided
      if (onAccordionClicked) {
        invokeAction({
          ...addToActionAnalytics(onAccordionClicked, {
            detail: newExpandedState
              ? ACCORDION_ANALYTICS_DETAIL.EXPANDED
              : ACCORDION_ANALYTICS_DETAIL.COLLAPSED,
            content: {
              id: accordionId,
              name: accordionId,
            },
            resource: {
              id: accordionId,
              name: accordionId,
              type: 'accordion',
            },
          }),
          payload: { accordionId, expanded: newExpandedState },
        });
      }
    },
    [networks, invokeAction, nodeId, onAccordionClicked],
  );

  // Handle tooltip click
  const handleTooltipClick = React.useCallback(
    (tooltipId: string) => {
      if (onTooltipClicked) {
        invokeAction({
          ...addToActionAnalytics(onTooltipClicked, {
            content: {
              id: tooltipId,
              name: tooltipId,
            },
          }),
          payload: { tooltipId },
        });
      }
    },
    [invokeAction, onTooltipClicked],
  );

  const currentNetwork = React.useMemo(() => {
    if (networks.length > 1) {
      return networks.find(
        (network) => network.networkType === selectedNetwork,
      );
    }
    return networks[0];
  }, [networks, selectedNetwork]);

  return (
    <StackLayout
      as="section"
      aria-labelledby={headingText ? headingId : undefined}
      spacing="$oneAndHalf"
    >
      {headingText && (
        <SectionHeaderWithIcon
          heading={headingText}
          headingLevel={headingLevel}
          icon={headingLeadingIcon}
          id={headingId}
        />
      )}

      {/* Network Selection with Tab Panels */}
      {networks.length > 1 ? (
        <SegmentedControl.Root
          // TODO: Create an accessible name for the segmented control
          segmentedControlName=""
          defaultSelectedControlId={selectedNetwork}
          value={selectedNetwork}
        >
          <StackLayout spacing="$oneAndHalf">
            <SegmentedControl.List>
              {networks.map((network) => (
                <SegmentedControl.Button
                  key={network.networkType}
                  value={network.networkType || ''}
                  onClick={handleNetworkClick({
                    networkType: network.networkType || '',
                    networkLabel: network.label || '',
                  })}
                >
                  {network.label}
                </SegmentedControl.Button>
              ))}
            </SegmentedControl.List>

            {/* Tab Panels for each network */}
            {networks.map(({ networkType, description, planUsage }) => (
              <Tabs.TabPanel
                key={`panel-${networkType}`}
                value={networkType || ''}
                css={{ width: '100%', padding: '$none' }}
              >
                <StackLayout spacing="$oneAndHalf">
                  {description && <ParagraphText>{description}</ParagraphText>}
                  {/* Plan Usage Content for this network */}
                  <PlanUsageContainer>
                    {planUsage?.map((plan, planIndex) => (
                      <Box
                        key={`plan-usage-${plan.id || planIndex}`}
                        css={{
                          width: '100%',
                        }}
                      >
                        {/* Accordion wrapper for expandable sections */}
                        {plan.accordion ? (
                          <AccordionPlanUsage
                            heading={plan.heading?.heading}
                            headingLevel={headingLevel}
                            description={plan.description}
                            id={plan.id}
                            accordion={plan.accordion}
                            tiers={plan.tiers}
                            onAccordionClick={() =>
                              handleAccordionClick(
                                plan.accordion?.analyticsId || '',
                                !plan.accordion?.isExpanded,
                              )
                            }
                            onTooltipClick={handleTooltipClick}
                            css={{
                              // Accordions have internal padding, so we need to offset the margin to account for the container padding.
                              marginBlockStart:
                                planIndex === 0
                                  ? 'calc($oneAndHalf * -1)'
                                  : undefined,
                              marginBlockEnd:
                                planIndex === planUsage.length - 1
                                  ? 'calc($oneAndHalf * -1)'
                                  : undefined,
                            }}
                          />
                        ) : (
                          <StaticPlanUsage
                            heading={plan.heading?.heading}
                            headingLevel={headingLevel}
                            description={plan.description}
                            tiers={plan.tiers}
                            onTooltipClick={handleTooltipClick}
                          />
                        )}
                      </Box>
                    ))}
                  </PlanUsageContainer>
                </StackLayout>
              </Tabs.TabPanel>
            ))}
          </StackLayout>
        </SegmentedControl.Root>
      ) : (
        /* Single network - render content directly */
        <StackLayout spacing="$oneAndHalf" css={{ width: '100%' }}>
          {currentNetwork?.description && (
            <ParagraphText>{currentNetwork.description}</ParagraphText>
          )}
          <PlanUsageContainer>
            {currentNetwork?.planUsage?.map((plan, planIndex) => (
              <Box
                key={`plan-usage-${plan.id || planIndex}`}
                css={{
                  width: '100%',
                }}
              >
                {/* Accordion wrapper for expandable sections */}
                {plan.accordion ? (
                  <AccordionPlanUsage
                    heading={plan.heading?.heading}
                    headingLevel={headingLevel}
                    headingSize={plan.heading?.headingSize}
                    description={plan.description}
                    id={plan.id}
                    accordion={plan.accordion}
                    tiers={plan.tiers}
                    onAccordionClick={() =>
                      handleAccordionClick(
                        plan.accordion?.analyticsId || '',
                        !plan.accordion?.isExpanded,
                      )
                    }
                    onTooltipClick={handleTooltipClick}
                    css={{
                      // Accordions have internal padding, so we need to offset the margin to account for the container padding.
                      marginBlockStart:
                        planIndex === 0 ? 'calc($oneAndHalf * -1)' : undefined,
                      marginBlockEnd:
                        currentNetwork.planUsage?.length &&
                        planIndex === currentNetwork.planUsage.length - 1
                          ? 'calc($oneAndHalf * -1)'
                          : undefined,
                    }}
                  />
                ) : (
                  <StaticPlanUsage
                    heading={plan.heading?.heading}
                    headingLevel={headingLevel}
                    headingSize={plan.heading?.headingSize}
                    description={plan.description}
                    tiers={plan.tiers}
                    onTooltipClick={handleTooltipClick}
                  />
                )}
              </Box>
            ))}
          </PlanUsageContainer>
        </StackLayout>
      )}
    </StackLayout>
  );
}
